import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useEffect } from "react";
let keyHandlers = [];
let anyKeyHandlers = {};
const keyRename = {
    ' ': 'Space',
    '\u00a0': 'Space',
};
/**
 * Registers a global keyboard shortcut handler. Multiple handlers can coexist; they are
 * dispatched by priority (highest first). If two handlers share the same priority, both
 * can handle the same key in one event.
 *
 * Re-registering with the same `name` replaces the previous handler.
 *
 * Key format (produced by `getKeyName()`):
 *   - Plain keys use `KeyboardEvent.key` values: `'a'`, `'Enter'`, `'ArrowDown'`, `'Escape'`
 *   - Space bar is renamed to `'Space'`
 *   - Modifiers are prefixed alphabetically, joined with `+`: `'Alt+m'`, `'Shift+Space'`,
 *     `'Alt+Control+s'`, `'Meta+z'`
 *   - Modifier keys themselves (e.g. pressing Alt alone) are just `'Alt'`
 *
 * @param name    Unique identifier. Used by `unregisterKeyHandler()` and to replace on re-register.
 * @param keys    Map of key strings to handler functions. Return `true` to mark the event as
 *                handled (calls `preventDefault`/`stopPropagation`). Return `false`/`undefined`
 *                to let it propagate.
 * @param blocks  If `true`, no lower-priority handlers will run regardless of whether this
 *                handler matched. Useful for modals.
 * @param priority Higher values are served first. Default `0`.
 */
export function registerKeyHandler(name, keys, blocks = false, priority = 0) {
    const cleanHandlers = keyHandlers.filter((h) => h.name != name);
    keyHandlers = [...cleanHandlers, { name, keys, priority, blocks }].sort((a, b) => b.priority - a.priority);
}
/** Removes the handler registered under `name`. Safe to call if not registered. */
export function unregisterKeyHandler(name) {
    keyHandlers = keyHandlers.filter((h) => h.name != name);
}
/** Registers a handler that is called for every keydown, regardless of key. Not affected by priority/blocking. */
export function registerAnyKeyHandler(name, handler) {
    anyKeyHandlers[name] = handler;
}
export function unregisterAnyKeyHandler(name) {
    delete anyKeyHandlers[name];
}
function handleKey(key) {
    var _a, _b;
    for (const name in anyKeyHandlers)
        anyKeyHandlers[name](key);
    let handledPrio = undefined;
    for (const handler of keyHandlers) {
        if (handledPrio != undefined && handler.priority != handledPrio)
            break;
        if ((_b = (_a = handler.keys)[key]) === null || _b === void 0 ? void 0 : _b.call(_a, key))
            handledPrio = handler.priority;
        if (handler.blocks) {
            break;
        }
    }
    return handledPrio != undefined;
}
/** Converts a keyboard event into the canonical key string used by handlers (e.g. `'Alt+m'`, `'Shift+Space'`). */
export function getKeyName(ev) {
    var _a;
    const modifiers = (ev.altKey || ev.ctrlKey || ev.metaKey || ev.shiftKey) ? [ev.altKey && 'Alt', ev.ctrlKey && 'Control', ev.metaKey && 'Meta', ev.shiftKey && 'Shift'].filter(it => it && it != ev.key).join('+') : '';
    let renamedKey = (_a = keyRename[ev.key]) !== null && _a !== void 0 ? _a : ev.key;
    // On macOS, Alt produces special characters (e.g. Alt+h → ˙). Use ev.code to get the physical key.
    if (ev.altKey && ev.code) {
        if (ev.code.startsWith('Key'))
            renamedKey = ev.code.charAt(3).toLowerCase() + ev.code.slice(4);
        else if (ev.code.startsWith('Digit'))
            renamedKey = ev.code.slice(5);
    }
    return modifiers ? modifiers + '+' + renamedKey : renamedKey;
}
/**
 * Wrap the app in this component to enable global keyboard shortcut dispatch.
 * Listens for `keydown` on `window` and routes events through registered handlers.
 */
const GlobalKeyHandler = ({ children }) => {
    function handleFlowGlobalKey(ev) {
        if (handleKey(getKeyName(ev))) {
            ev.preventDefault();
            ev.stopPropagation();
        }
    }
    useEffect(() => {
        window.addEventListener("keydown", handleFlowGlobalKey, false);
        return () => {
            window.removeEventListener("keydown", handleFlowGlobalKey, false);
            keyHandlers = [];
        };
    }, []);
    return _jsx(_Fragment, { children: children });
};
export default GlobalKeyHandler;
